import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface MobileLanguageSelectorProps {
  isScrolled?: boolean;
}

const MobileLanguageSelector = ({
  isScrolled = false,
}: MobileLanguageSelectorProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'pt-BR', name: 'Português', flag: '🇧🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
  ];

  const currentLanguage =
    languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };

  // Calcular posição do dropdown quando abrir
  const handleOpenDropdown = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: Math.max(rect.width, 200),
      });
    }
    setIsOpen(!isOpen);
  };

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className='relative'>
      {/* Botão do Language Selector Mobile */}
      <button
        ref={buttonRef}
        onClick={handleOpenDropdown}
        className='w-full flex items-center justify-between px-4 py-3 bg-avoprime-primary/10 rounded-lg hover:bg-avoprime-primary/20 transition-colors duration-200 border border-avoprime-primary/20'
      >
        <div className='flex items-center space-x-3'>
          <GlobeAltIcon className='w-5 h-5 text-avoprime-primary' />
          <span className='text-lg'>{currentLanguage.flag}</span>
          <span className='text-sm font-medium text-avoprime-primary'>
            {currentLanguage.name}
          </span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className='text-avoprime-primary'
        >
          ▼
        </motion.div>
      </button>

      {/* Dropdown renderizado como portal */}
      {isOpen &&
        dropdownPosition.width > 0 &&
        createPortal(
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className='fixed bg-white rounded-lg shadow-xl border border-gray-200 z-[90] overflow-hidden'
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              minWidth: '200px',
            }}
          >
            <div className='py-2'>
              {languages.map(language => (
                <button
                  key={language.code}
                  onClick={() => changeLanguage(language.code)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-avoprime-primary/5 transition-colors duration-200 ${
                    i18n.language === language.code
                      ? 'bg-avoprime-primary/10 text-avoprime-primary border-l-4 border-l-avoprime-primary'
                      : 'text-gray-700 hover:text-avoprime-primary'
                  }`}
                >
                  <span className='text-lg'>{language.flag}</span>
                  <span className='text-sm font-medium'>{language.name}</span>
                  {i18n.language === language.code && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className='w-2 h-2 bg-avoprime-primary rounded-full ml-auto'
                    />
                  )}
                </button>
              ))}
            </div>
          </motion.div>,
          document.body
        )}
    </div>
  );
};

export default MobileLanguageSelector;
